import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Sparkles, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import {
    PACKAGES,
    BOOKABLE_SERVICES,
    PERSONALIZED_JOURNEY,
    IMAGES,
} from "../data/content";
import useReveal from "../hooks/useReveal";

/**
 * PackageDetailPage
 * Renders a single offering by slug.
 * Handles three shapes:
 *  1. Bookable service (pre-talk / one-on-one): price + booking CTA
 *  2. PERSONALIZED_JOURNEY: 8-session list
 *  3. Simple package (from PACKAGES array): eyebrow, description, includes[], idealFor
 */
export default function PackageDetailPage() {
    useReveal();
    const { slug } = useParams();

    if (slug === PERSONALIZED_JOURNEY.slug) {
        return <PersonalizedDetail />;
    }

    const bookable = BOOKABLE_SERVICES.find(
        (b) => b.slug === slug && b.slug !== PERSONALIZED_JOURNEY.slug,
    );
    if (bookable) {
        return <BookableServiceDetail svc={bookable} />;
    }

    const pkg = PACKAGES.find((p) => p.slug === slug);
    if (!pkg) return <Navigate to="/packages" replace />;

    return <SimplePackageDetail pkg={pkg} />;
}

/* ---------- Shared header ---------- */
function BackLink() {
    return (
        <Link
            to="/packages"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#A68A6B] hover:text-[#897157] transition-colors"
            data-testid="package-back"
        >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
            All programs
        </Link>
    );
}

/* ---------- Simple package (3 base ones) ---------- */
function SimplePackageDetail({ pkg }) {
    return (
        <div className="pt-24" data-testid={`package-detail-${pkg.slug}`}>
            <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
                <BackLink />
                <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start ssh-reveal">
                    <div className="lg:col-span-7">
                        <p className="ssh-eyebrow">{pkg.eyebrow}</p>
                        <h1 className="mt-5 font-serif font-light tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl text-[#2C2B29]">
                            {pkg.name}
                        </h1>
                        <p className="mt-8 text-lg leading-relaxed text-[#4A4947] max-w-2xl">
                            {pkg.description}
                        </p>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="aspect-[4/5] overflow-hidden">
                            <img
                                src={IMAGES.calmNature}
                                alt=""
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-[#FAF8F5] border-t border-[#EFEBE4]">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 ssh-reveal">
                    <div>
                        <p className="ssh-eyebrow">What&apos;s included</p>
                        <ul className="mt-8 space-y-4 text-base lg:text-lg text-[#2C2B29]">
                            {pkg.includes.map((x) => (
                                <li key={x} className="flex gap-3">
                                    <span className="text-[#A68A6B]">—</span>
                                    <span>{x}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <p className="ssh-eyebrow">Ideal for</p>
                        <p className="mt-8 font-serif text-2xl lg:text-3xl leading-snug text-[#2C2B29]">
                            {pkg.idealFor}
                        </p>
                        <div className="mt-10">
                            <Link to="/contact#booking">
                                <Button
                                    className="ssh-btn-primary rounded-none px-9 py-7 text-xs uppercase tracking-[0.22em]"
                                    data-testid={`pkg-cta-${pkg.slug}`}
                                >
                                    Inquire About This Journey
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            <RelatedPrograms currentSlug={pkg.slug} />
        </div>
    );
}

/* ---------- Bookable paid service (pre-talk / one-on-one) ---------- */
function BookableServiceDetail({ svc }) {
    return (
        <div className="pt-24" data-testid={`package-detail-${svc.slug}`}>
            <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
                <BackLink />
                <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start ssh-reveal">
                    <div className="lg:col-span-7">
                        <p className="ssh-eyebrow">{svc.eyebrow}</p>
                        <h1 className="mt-5 font-serif font-light tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl text-[#2C2B29]">
                            {svc.name}
                        </h1>
                        <p className="mt-4 text-xs uppercase tracking-[0.18em] text-[#8A9A86]">
                            {svc.duration}
                        </p>
                        <p className="mt-8 text-lg leading-relaxed text-[#4A4947] max-w-2xl">
                            {svc.description}
                        </p>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="aspect-[4/5] overflow-hidden">
                            <img
                                src={IMAGES.calmNature}
                                alt=""
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-[#FAF8F5] border-t border-[#EFEBE4]">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 ssh-reveal">
                    <div>
                        <p className="ssh-eyebrow">What&apos;s included</p>
                        <ul className="mt-8 space-y-4 text-base lg:text-lg text-[#2C2B29]">
                            {svc.includes.map((x) => (
                                <li key={x} className="flex gap-3">
                                    <span className="text-[#A68A6B]">—</span>
                                    <span>{x}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <p className="ssh-eyebrow">Ideal for</p>
                        <p className="mt-8 font-serif text-2xl lg:text-3xl leading-snug text-[#2C2B29]">
                            {svc.idealFor}
                        </p>
                    </div>
                </div>
                <div className="max-w-7xl mx-auto px-6 lg:px-12 pb-20 lg:pb-28">
                    <div className="bg-white border border-[#EFEBE4] p-8 lg:p-10 flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-12 ssh-reveal">
                        <div className="lg:flex-1">
                            <p className="ssh-eyebrow">Investment</p>
                            <div className="mt-3 flex items-baseline gap-4">
                                <span className="font-serif text-5xl lg:text-6xl leading-none text-[#2C2B29]">
                                    {svc.priceLabel}
                                </span>
                                <span className="text-sm text-[#75736F]">
                                    {svc.duration}
                                </span>
                            </div>
                            <p className="mt-4 text-sm text-[#4A4947] leading-relaxed max-w-md">
                                Pick a date & time that suits you — your booking
                                is confirmed instantly on secure payment via
                                Razorpay.
                            </p>
                        </div>
                        <div>
                            <Link
                                to={`/contact?service=${svc.slug}#booking`}
                            >
                                <Button
                                    className="ssh-btn-primary rounded-none px-9 py-7 text-xs uppercase tracking-[0.22em] inline-flex items-center gap-3 whitespace-nowrap"
                                    data-testid={`book-pay-${svc.slug}`}
                                >
                                    <ShieldCheck
                                        className="h-4 w-4"
                                        strokeWidth={1.5}
                                    />
                                    Book & Pay {svc.priceLabel}
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            <RelatedPrograms currentSlug={svc.slug} />
        </div>
    );
}

/* ---------- 8-Session Personalized detail ---------- */
function PersonalizedDetail() {
    return (
        <div
            className="pt-24"
            data-testid={`package-detail-${PERSONALIZED_JOURNEY.slug}`}
        >
            <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
                <BackLink />
                <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-12 ssh-reveal">
                    <div className="lg:col-span-7">
                        <p className="ssh-eyebrow">
                            {PERSONALIZED_JOURNEY.eyebrow}
                        </p>
                        <h1 className="mt-5 font-serif font-light tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl text-[#2C2B29]">
                            An 8-session{" "}
                            <em className="italic text-[#A68A6B]">
                                personalised healing journey.
                            </em>
                        </h1>
                    </div>
                    <div className="lg:col-span-5">
                        <p className="text-lg leading-relaxed text-[#4A4947]">
                            {PERSONALIZED_JOURNEY.intro}
                        </p>
                        <p className="mt-4 text-base leading-relaxed text-[#4A4947]">
                            {PERSONALIZED_JOURNEY.intro2}
                        </p>
                    </div>
                </div>
            </section>

            <section className="bg-[#FAF8F5] border-t border-[#EFEBE4]">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
                    <p className="ssh-eyebrow ssh-reveal">
                        What we&apos;ll explore
                    </p>
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#EFEBE4] border border-[#EFEBE4]">
                        {PERSONALIZED_JOURNEY.sessions.map((s, i) => (
                            <article
                                key={s.n}
                                className="bg-white p-8 lg:p-10 ssh-reveal"
                                style={{ transitionDelay: `${0.04 * i}s` }}
                                data-testid={`session-${s.n}`}
                            >
                                <div className="flex items-baseline gap-4">
                                    <span
                                        className="font-serif text-4xl lg:text-5xl leading-none text-[#A68A6B]"
                                        aria-hidden="true"
                                    >
                                        {s.n}
                                    </span>
                                    <div>
                                        <p className="ssh-eyebrow">Session</p>
                                        <h3 className="mt-1 font-serif text-xl lg:text-2xl leading-tight text-[#2C2B29]">
                                            {s.title}
                                        </h3>
                                    </div>
                                </div>
                                <ul className="mt-5 space-y-2 text-sm text-[#4A4947]">
                                    {s.items.map((it) => (
                                        <li key={it} className="flex gap-2 leading-relaxed">
                                            <span className="text-[#A68A6B]">—</span>
                                            <span>{it}</span>
                                        </li>
                                    ))}
                                </ul>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-white border-t border-[#EFEBE4]">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 ssh-reveal">
                    <div className="lg:col-span-5">
                        <p className="ssh-eyebrow">Modalities used</p>
                        <p className="mt-4 text-base text-[#4A4947]">
                            {PERSONALIZED_JOURNEY.modalitiesIntro}
                        </p>
                        <ul className="mt-6 flex flex-wrap gap-2">
                            {PERSONALIZED_JOURNEY.modalities.map((m) => (
                                <li
                                    key={m}
                                    className="text-xs uppercase tracking-[0.14em] text-[#2C2B29] border border-[#EFEBE4] px-3 py-2 bg-[#FAF8F5]"
                                >
                                    {m}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="lg:col-span-7">
                        <p className="ssh-eyebrow">This is for you if</p>
                        <ul className="mt-6 space-y-4">
                            {PERSONALIZED_JOURNEY.forYouIf.map((line) => (
                                <li
                                    key={line}
                                    className="flex gap-3 text-base lg:text-lg text-[#2C2B29] leading-relaxed"
                                >
                                    <Sparkles
                                        className="h-4 w-4 text-[#A68A6B] flex-shrink-0 mt-1.5"
                                        strokeWidth={1.25}
                                    />
                                    <span>{line}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            <section className="bg-[#FAF8F5] border-t border-[#EFEBE4]">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
                    <div className="bg-white border border-[#EFEBE4] p-8 lg:p-10 flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-12 ssh-reveal">
                        <div className="lg:flex-1">
                            <p className="ssh-eyebrow">Investment</p>
                            <div className="mt-3 flex items-baseline gap-4 flex-wrap">
                                <span className="font-serif text-5xl lg:text-6xl leading-none text-[#2C2B29]">
                                    {PERSONALIZED_JOURNEY.priceLabel}
                                </span>
                                <span className="text-sm text-[#75736F]">
                                    {PERSONALIZED_JOURNEY.priceCaption}
                                </span>
                            </div>
                            <p className="mt-4 text-base lg:text-lg text-[#2C2B29] leading-relaxed">
                                {PERSONALIZED_JOURNEY.investment}
                            </p>
                            <p className="mt-3 text-sm text-[#4A4947] leading-relaxed">
                                Book your first session below — your seat is
                                confirmed instantly on secure payment via
                                Razorpay. The remaining seven sessions are
                                scheduled together with Shreya.
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <Link to="/contact?service=personalized-8-session#booking">
                                <Button
                                    className="ssh-btn-primary rounded-none px-9 py-7 text-xs uppercase tracking-[0.22em] inline-flex items-center gap-3 whitespace-nowrap"
                                    data-testid="personalized-book-pay"
                                >
                                    <ShieldCheck
                                        className="h-4 w-4"
                                        strokeWidth={1.5}
                                    />
                                    Book & Pay {PERSONALIZED_JOURNEY.priceLabel}
                                </Button>
                            </Link>
                            <Link to="/contact?service=pre-talk#booking">
                                <Button
                                    variant="outline"
                                    className="rounded-none border-[#2C2B29] bg-transparent text-[#2C2B29] hover:bg-[#2C2B29] hover:text-white px-9 py-7 text-xs uppercase tracking-[0.22em] transition-colors whitespace-nowrap"
                                    data-testid="personalized-ask-first"
                                >
                                    Start with a Pre-Talk
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            <RelatedPrograms currentSlug={PERSONALIZED_JOURNEY.slug} />
        </div>
    );
}

/* ---------- Related programs strip ---------- */
export function RelatedPrograms({ currentSlug }) {
    const all = [
        ...BOOKABLE_SERVICES.map((b) => ({
            slug: b.slug,
            name: b.name,
            eyebrow: b.eyebrow,
        })),
        ...PACKAGES.map((p) => ({
            slug: p.slug,
            name: p.name,
            eyebrow: p.eyebrow,
        })),
    ];
    const others = all.filter((p) => p.slug !== currentSlug).slice(0, 3);

    return (
        <section className="bg-white border-t border-[#EFEBE4]">
            <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
                <p className="ssh-eyebrow text-center ssh-reveal">
                    Continue exploring
                </p>
                <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 ssh-reveal">
                    {others.map((p) => (
                        <Link
                            key={p.slug}
                            to={`/packages/${p.slug}`}
                            className="group p-6 border border-[#EFEBE4] hover:border-[#A68A6B] transition-colors"
                            data-testid={`related-${p.slug}`}
                        >
                            <p className="ssh-eyebrow">{p.eyebrow}</p>
                            <p className="mt-3 font-serif text-xl text-[#2C2B29] group-hover:text-[#A68A6B] transition-colors leading-tight">
                                {p.name}
                            </p>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
}
